import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { readEmbedToken } from '../../utils/embedToken';
import { Box, Typography } from '@mui/material';
import { useUser } from '../../context/UserContext';
import EmbeddedLayout from '../Layout/EmbeddedLayout';
import Login from '../Auth/Login';
import '../Embedded/Embedded.css';
import EmbeddedDashboard from './EmbeddedDashboard';
import LoadingSkeleton from '../common/LoadingSkeleton';
// Course components
import CoursePreview from '../Course/CoursePreview';
import AllInstances from '../Course/AllInstances';

// Learning Path components
import LearningPathLearnerView from '../LearningPath/LearningPathLearnerView';
import AllLPathInstances from '../LearningPath/AllLPathInstances';

// Catalog and My Learnings
import Catlog from '../Learner/Catlog';
import MyLearnings from '../Learner/MyLearnings';

// Assignment components
import AssignmentPreview from '../Assignments/AssignmentPreview';
import AssignmentSubmitForm from '../Assignments/AssignmentSubmitForm';

// Quiz components
import QuizPreview from '../Quiz/QuizPreview';
import QuizStart from '../Quiz/QuizStart';
import QuestionDisplay from '../Quiz/QuestionDisplay';
import ViewResult from '../Quiz/ViewResult';
import ResultDetails from '../Quiz/ResultDetails';

// Content Player
import ContentPlayer from '../Player/ContentPlayer';
import { ContentPlayerProvider } from '../../context/ContentPlayerContext';

const EmbeddedRoutes = () => {
  const { user, loading } = useUser();
  const location = useLocation();
  // The widget shows nothing until the server confirms that the signed-in learner's workspace owns the widget's token,
  // the same check the widget's search runs (K13 pt 17, embedSearchController). With the session cookie sent inside
  // other sites' frames (K13 pt 10), a learner of one workspace who opens another workspace's site would otherwise see
  // the widget's pages with their own workspace's content inside that site. 'pending', 'ok', 'signin' or 'other'. The
  // result is kept with the learner it was for, so a pass for one learner never shows the pages to another: until the
  // check has run for the signed-in learner, it reads 'pending'.
  const [check, setCheck] = useState({ userId: null, state: 'pending' });
  const isLearner = !!user && user.role_id === 2;
  const userId = user ? user.id : null; // the check runs again for another signed-in user
  const workspaceCheck = check.userId === userId ? check.state : 'pending';

  useEffect(() => {
    if (loading || !isLearner) return;
    const token = readEmbedToken();
    if (!token) { setCheck({ userId, state: 'other' }); return; }
    let cancelled = false;
    axios.get('/api/public/embed/session', { headers: { 'x-embed-token': token } })
      .then(() => { if (!cancelled) setCheck({ userId, state: 'ok' }); })
      .catch((err) => {
        if (cancelled) return;
        // Signed out on the server (the session ended): the embedded sign-in. Anything else: nothing.
        setCheck({ userId, state: err?.response?.status === 401 && err?.response?.data?.error === 'signin_required' ? 'signin' : 'other' });
      });
    return () => { cancelled = true; };
  }, [loading, isLearner, userId]);

  // Function to preserve embed parameters across navigation
  const getEmbedParams = () => {
    const params = new URLSearchParams(location.search);
    const embedParams = {};
    
    // Extract all embed-related parameters
    ['embedded', 'theme', 'ml_type_filter', 'ml_sorting_filter', 'ml_grid_list', 
     'cat_type_filter', 'cat_sorting_filter', 'cat_grid_list'].forEach(key => {
      if (params.has(key)) {
        embedParams[key] = params.get(key);
      }
    });
    
    return embedParams;
  };

  // Store embed params to pass to child routes
  const embedParams = getEmbedParams();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingSkeleton rows={6} height={56} />
      </Box>
    );
  }
  
  // If not authenticated or not a learner, show the embedded login
  if (!user || user.role_id !== 2) { // Learner role_id is 2
    // If user is logged in but not a learner, show error message
    if (user && user.role_id !== 2) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', p: 3 }}>
          <Typography variant="h6" color="error" gutterBottom align="center">
            Access restricted
          </Typography>
          <Typography variant="body2" align="center">
            Only learners can access this content. Please log in with a learner account.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <button 
              onClick={() => {
                localStorage.removeItem('isAuthenticated');
                localStorage.removeItem('userData');
                // Stay in embedded mode, keeping the embed parameters (the token decides where the page may be shown,
                // K13). Removed 11 Oct 2026 (K13): reloading /embed without them.
                window.location.href = `/embed${window.location.search}`;
              }}
              className="login-btn"
            >
              Try Again
            </button>
          </Box>
        </Box>
      );
    }
    
    // Otherwise show embedded login
    return (
      <Box sx={{ p: 2 }}>
        <Login isEmbedded={true} />
      </Box>
    );
  }
  
  // The workspace check (K13 pt 17): wait for it, then show the routes only when it passed.
  if (workspaceCheck === 'pending') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingSkeleton rows={6} height={56} />
      </Box>
    );
  }
  if (workspaceCheck === 'signin') {
    return (
      <Box sx={{ p: 2 }}>
        <Login isEmbedded={true} />
      </Box>
    );
  }
  if (workspaceCheck !== 'ok') {
    return <Box />;
  }

  return (
    <Routes>
      <Route element={<EmbeddedLayout />}>
        {/* Dashboard */}
        <Route path="/" element={<EmbeddedDashboard />} />
        <Route path="/dashboard" element={<EmbeddedDashboard />} />
        
        {/* Catalog and My Learnings */}
        <Route path="/catalog" element={<Catlog />} />
        <Route path="/my-learnings" element={<MyLearnings />} />
        
        {/* Course Routes */}
        <Route path="/learner/course/:id" element={<CoursePreview />} />
        <Route path="/learner/course/:id/:instanceId" element={<CoursePreview />} />
        <Route path="/learner/course/:id/instances" element={<AllInstances />} />
        
        {/* Learning Path Routes */}
        <Route path="/learner/lpath/:id" element={<LearningPathLearnerView />} />
        <Route path="/learner/lpath/:id/:instanceId" element={<LearningPathLearnerView />} />
        <Route path="/learner/lpath/:id/instances" element={<AllLPathInstances />} />
        
        {/* Assignment Routes */}
        <Route path="/assignments/preview/:assignment_id/:courseId/:ccrId" element={<AssignmentPreview />} />
        <Route path="/assignments/preview/:assignment_id" element={<AssignmentPreview />} />
        <Route path="/assignments/submit/:assignment_id/:courseId/:ccrId" element={<AssignmentSubmitForm />} />
        <Route path="/assignments/submit/:assignment_id" element={<AssignmentSubmitForm />} />
        
        {/* Quiz Routes */}
        <Route path="/quiz/preview/:quizId/:courseId/:ccrId" element={<QuizPreview />} />
        <Route path="/quiz/preview/:quizId" element={<QuizPreview />} />
        <Route path="/quiz/start/:quizId/:courseId/:ccrId" element={<QuizStart />} />
        <Route path="/quiz/question/:quizId/:courseId/:ccrId" element={<QuestionDisplay />} />
        <Route path="/quiz/result/:quizId" element={<ViewResult />} />
        <Route path="/quiz/result-details/:quiz_id" element={<ResultDetails />} />
        
        {/* Content Player */}
        <Route 
          path="/content/:courseId/:contentId" 
          element={
            <ContentPlayerProvider>
              <ContentPlayer />
            </ContentPlayerProvider>
          } 
        />
        
        {/* Fallback route */}
        {/* Keeps the embed parameters, the token among them (K13): the page is only shown inside the token's sites, so a
            later reload without them is refused there. Removed 11 Oct 2026 (K13): dropping them on the way. */}
        <Route path="*" element={<Navigate to={`/embed/dashboard${location.search}`} replace />} />
      </Route>
    </Routes>
  );
};

export default EmbeddedRoutes;